import { useFormContext, BuildHtmlElement } from "./formElements";
import {
  Button,
  Col,
  Form,
  InputGroup,
  Row,
  Table,
  Alert,
} from "react-bootstrap";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faObjectGroup,
  faObjectUngroup,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { BuildingMeasurementsTable } from "./BuildingMeasurementsTable";
import { CurrentMeasureTable } from "./CurrentMeasureTable";
import { FileUploader } from "./FileUploader";
import { FansInspectionChecklist } from "./FansInspectionChecklist";
import "./TableSelect.css";

// komórki, których nie wolno scalać: piętro (numeracja/sortowanie kondygnacji),
// netto (osobna tabela kryterium 1 w PDF) oraz selecty z wynikami
const isMergeableCell = (cell, index) =>
  index !== 0 &&
  cell.type !== "select" &&
  cell.name !== "floor" &&
  cell.name !== "net";

// numer sekcji nagłówka dla każdej kolumny danych - scalać można tylko w obrębie sekcji
const columnSections = (header) => {
  const sections = [];
  header.forEach((h, index) => {
    const width = h.type === "rowSpan" ? h.value?.length ?? 0 : 1;
    for (let i = 0; i < width; i++) sections.push(index);
  });
  return sections;
};

// rozdziela scalenie obejmujące kolumnę `index` (scalenie: komórka startowa ma
// colspan: N, kolejne N-1 mają merged: true)
const splitMergeAt = (row, index) => {
  let start = index;
  while (start > 0 && row[start]?.merged) start--;

  const span = row[start]?.colspan ?? 1;
  if (span <= 1 || start + span <= index) return row;

  const result = [...row];
  const { colspan, ...first } = result[start];
  result[start] = first;
  for (let c = start + 1; c < start + span && c < result.length; c++) {
    const { merged, ...rest } = result[c];
    result[c] = rest;
  }
  return result;
};

export function ThText({ text, className, onChange }) {
  return (
    <th rowSpan={2} className={className}>
      <Form.Control
        className={"border-0 bg-transparent aeration-header-control"}
        as="textarea"
        value={text}
        onChange={onChange}
      />
    </th>
  );
}

// ⬇️ ThSpan z przyciskiem „+” i dynamicznym colSpan
export function ThSpan({ label, onChange, onAdd, colSpan }) {
  return (
    <th
      colSpan={colSpan}
      className="text-center thick-border-right"
    >
      <div className="d-flex align-items-center justify-content-center gap-2">
        <Form.Control
          className={"border-0 bg-transparent aeration-header-control"}
          as="textarea"
          value={label}
          onChange={onChange}
        />
        <Button
          variant="outline-secondary"
          size="sm"
          className="p-1 d-inline-flex align-items-center justify-content-center"
          title="Dodaj kolumnę w tej sekcji"
          onClick={onAdd}
        >
          +
        </Button>
      </div>
    </th>
  );
}

export const getSectionEnds = (header) => {
  const ends = [];
  let cursor = 0;
  header.forEach((h) => {
    const width = h.type === "rowSpan" ? h.value?.length ?? 0 : 1;
    cursor += width;
    ends.push(cursor - 1);
  });
  return ends;
};

export const isSectionEnd = (subIndex, h) => {
  const width = h.value?.length ?? 0;
  return subIndex === width - 1; // ostatnia subkomórka w sekcji
};

export function CloneButton(props) {
  const { onChange } = useFormContext();
  const element = props.value;
  const deepCopyAllowed = element.deepCopyAllowed !== false;

  const children = [];

  if (!Array.isArray(element.children)) {
    children.push(element.children);
  } else {
    for (let x in element.children) {
      const child = element.children[x];
      children.push(<BuildHtmlElement key={x} element={child} />);
    }
  }

  const cloneElement = () => {
    const newElement = JSON.parse(
      JSON.stringify(element.cloneable_element)
    );
    element.children.push(newElement);
    // trigger re-render
    onChange({ ...element });
  };

  const cloneLastElement = () => {
    const newElement = JSON.parse(
      JSON.stringify(element.children[element.children.length - 1])
    );
    element.children.push(newElement);
    onChange({ ...element });
  };

  return (
    <div className={element.class}>
      {children}
      <Button variant="info" size="" className="m-2" onClick={cloneElement}>
        {element.label}
      </Button>
      {deepCopyAllowed ? (
        <Button variant="info" size="" className="m-2" onClick={cloneLastElement}>
          Kopiuj jednostkę
        </Button>
      ) : null}
    </div>
  );
}

function MeasurementsCriteria(props) {
  const element = props.value;
  const { onChange } = useFormContext();
  const hasChecked = Object.prototype.hasOwnProperty.call(element, "checked");
  const showContent = !hasChecked || element.checked;

  return (
    <div className="d-flex gap-2 mt-2">
      <div className="aeration-criteria-marker">
        {hasChecked ? (
          <Form.Check
            type="checkbox"
            checked={element.checked}
            onChange={(event) => {
              element.checked = event.target.checked;
              onChange(element);
            }}
          />
        ) : (
          <span className="aeration-criteria-dot" />
        )}
      </div>
      <div className="d-flex flex-column flex-grow-1">
        <Form.Control
          className={"border-0 bg-transparent mb-0 h5 p-0"}
          type="text"
          value={element.title}
          placeholder="Tytuł kryterium"
          onChange={(event) => {
            element.title = event.target.value;
            onChange(element);
          }}
        />
        <div className={`aeration-criteria-content ${showContent ? "show" : ""}`}>
          <div className="aeration-criteria-content-inner">
            <Form.Control
              className={"bg-transparent "}
              type={element.type}
              value={element.value}
              onChange={(event) => {
                const newVal = event.target.value;
                element.value = newVal;
                onChange(element);
              }}
            />
            <Form.Control
              className={"bg-transparent mt-2"}
              as={element.description.type}
              value={element.description.value}
              placeholder="Opis kryterium pomiarowego"
              onChange={(event) => {
                const newVal = event.target.value;
                element.description.value = newVal;
                onChange(element);
              }}
            />
            
            {
              element.table ? (
                <BuildingMeasurementsTable value={element.table} />
              ) : null
            }
            {element.fans_inspection_checklist ? (
              <FansInspectionChecklist value={element.fans_inspection_checklist} />
            ) : null}
            {element.table_current_measure ? (
              <CurrentMeasureTable value={element.table_current_measure} />
            ) : null}
            {
              element.file_uploader ? (
                <FileUploader value={element.file_uploader} />
              ) : null
            }
          </div>
        </div>
      </div>
    </div>
  );
}

export function AerationTable(props) {
  const element = props.value;
  const { onChange, onDelete } = useFormContext();
  const header = element.table_head;
  const values = element.value;
  const floorTempltate = element.columns;
  const [customFloor, setCustomFloor] = useState("");
  const [error, setError] = useState(null);
  const sectionEnds = getSectionEnds(header);
  const sections = columnSections(header);

  // komórka może scalić się z prawą, gdy nie jest zakryta, obie są "zwykłymi" polami
  // i cały nowy zakres leży w jednej sekcji nagłówka
  const canMergeRight = (row, cellIndex) => {
    const cell = row[cellIndex];
    if (cell.merged || !isMergeableCell(cell, cellIndex)) return false;
    const next = cellIndex + (cell.colspan ?? 1);
    if (next >= row.length || !isMergeableCell(row[next], next)) return false;
    const last = next + (row[next].colspan ?? 1) - 1;
    return sections[cellIndex] === sections[last];
  };

  const handleMergeRight = (rowIndex, cellIndex) => {
    const row = [...values[rowIndex]];
    const cell = row[cellIndex];
    const next = cellIndex + (cell.colspan ?? 1);
    const nextCell = row[next];
    const span = (cell.colspan ?? 1) + (nextCell.colspan ?? 1);

    row[cellIndex] = {
      ...cell,
      value: cell.value !== "" ? cell.value : nextCell.value,
      colspan: span,
    };
    for (let c = cellIndex + 1; c < cellIndex + span; c++) {
      const { colspan, ...rest } = row[c];
      row[c] = { ...rest, value: "", merged: true };
    }

    element.value[rowIndex] = row;
    onChange({ ...element });
  };

  const handleSplit = (rowIndex, cellIndex) => {
    element.value[rowIndex] = splitMergeAt(values[rowIndex], cellIndex);
    onChange({ ...element });
  };

  const getRow = (index) => {
    let cols = [];
    const data = values[index];

    const handleDeleteRow = () => {
      element.value.splice(index, 1);
      onChange({ ...element });
    };

    for (let x = 0; x < Object.keys(data).length; x++) {
      const column = data[x];
      if (column.merged) continue;

      const span = column.colspan ?? 1;
      // ⬇️ ZAMIANA: gruba krawędź po końcu każdej sekcji (dla scalonej - po ostatniej kolumnie zakresu)
      const thickBorderClass = sectionEnds.includes(x + span - 1)
        ? "thick-border-right"
        : "";

      if (column.type === "select") {
        cols.push(
          <td className={"p-0 border-2 " + thickBorderClass} key={x}>
            <Form.Select
              className={"border-0 bg-transparent "}
              aria-label={column.label}
              value={column.value}
              onChange={(event) => {
                const newVal = event.target.value;
                const selectedOption = column.options.find((option) => option.value === newVal);

                if (selectedOption?.disable_column === true) {
                  element.value = element.value.map((row) => {
                    row[x] = { ...row[x], value: newVal };
                    return row;
                  });
                } else {
                  data[x] = { ...column, value: newVal };
                  element.value[index] = data;
                }

                onChange(element);
              }}
            >
              <option value=""></option>
              {column.options?.map((opt, i) => {
                return (
                  <option key={i} value={opt.value}>
                    {opt.value} - {opt.label}
                  </option>
                );
              })}
            </Form.Select>
          </td>
        );
      } else {
        const isMerged = span > 1;
        cols.push(
          <td
            className={"p-0 border-2 table-select-td " + thickBorderClass}
            key={x}
            colSpan={span}
          >
            <Form.Control
              className={`border-0 bg-transparent ${isMerged ? "text-center" : ""}`}
              type={column.type}
              value={column.value}
              placeholder={column.label}
              onChange={(event) => {
                const newVal = event.target.value;
                data[x] = { ...column, value: newVal };
                element.value[index] = data;
                onChange(element);
              }}
            />
            <div className="table-select-cell-actions">
              {isMerged && (
                <button
                  type="button"
                  title="Rozdziel komórki"
                  onClick={() => handleSplit(index, x)}
                >
                  <FontAwesomeIcon icon={faObjectUngroup} />
                </button>
              )}
              {canMergeRight(data, x) && (
                <button
                  type="button"
                  title="Scal z komórką po prawej"
                  onClick={() => handleMergeRight(index, x)}
                >
                  <FontAwesomeIcon icon={faObjectGroup} />
                </button>
              )}
            </div>
          </td>
        );
      }
    }

    return (
      <tr key={index}>
        {cols}
        <td>
          <div className={"btn-close"} onClick={handleDeleteRow}></div>
        </td>
      </tr>
    );
  };

  const onChangeHeaderLabel = (index, event) => {
    element.table_head[index].label = event.target.value;
    onChange({ ...element });
  };

  const onChangeSubHeaderLabel = (headerIndex, subIndex, event) => {
    element.table_head[headerIndex].value[subIndex].label = event.target.value;
    onChange({ ...element });
  };

  // ⬇️ Dodawanie nowej kolumny w konkretnej sekcji (rowSpan) — domyślnie jako PRZEDOSTATNIEJ,
  //    albo na pozycji `atSubIndex` w sekcji
  const addColumnToSection = (headerIndex, atSubIndex) => {
    // 1) policz globalny indeks startu sekcji i jej długość
    let startIdx = 0;
    for (let i = 0; i < headerIndex; i++) {
      const h = header[i];
      startIdx += h.type === "text" ? 1 : h.value?.length ?? 0;
    }
    const sectionLen = header[headerIndex].value?.length ?? 0;

    // 2) pozycja wstawienia w danych: przedostatnia w sekcji
    //    - gdy sekcja pusta: wstaw na jej początku
    //    - gdy sekcja ma 1 kolumnę: wstaw przed nią (stanie się pierwszą z dwóch)
    const insertPosInSection =
      atSubIndex ?? (sectionLen > 0 ? sectionLen - 1 : 0);
    const insertPos = startIdx + insertPosInSection;

    // 3) zaktualizuj nagłówek: wstaw subkolumnę w odpowiednim miejscu
    const newSubLabel = `Nowa kolumna ${sectionLen + 1}`;
    const newSub = { label: newSubLabel, type: "text" };

    const oldSubs = header[headerIndex].value ?? [];
    const newSubs = [
      ...oldSubs.slice(0, insertPosInSection),
      newSub,
      ...oldSubs.slice(insertPosInSection),
    ];
    header[headerIndex].value = newSubs;

    // 4) przygotuj szablon nowej kolumny na bazie sąsiadów w sekcji
    //    preferujemy "stary" ostatni element sekcji (pozycja insertPos),
    //    w przeciwnym razie bierzemy poprzedni (insertPos - 1), inaczej fallback
    const pickTemplate = (row) => {
      return { type: "text", label: "" };
    };

    const newColTemplateFromFirstRow = pickTemplate(values?.[0]);
    const newColTemplate = {
      ...newColTemplateFromFirstRow,
      value: "",
      label: newSubLabel,
    };

    // 5) wstaw nową kolumnę w KAŻDYM wierszu na pozycji insertPos
    //    (scalenie przecinające miejsce wstawienia jest rozdzielane)
    element.value = values.map((source) => {
      const row = source[insertPos]?.merged
        ? splitMergeAt(source, insertPos)
        : source;
      const before = row.slice(0, insertPos);
      const after = row.slice(insertPos);
      return [...before, { ...newColTemplate }, ...after];
    });

    element.columns = [
      ...element.columns.slice(0, insertPos),
      newSub,
      ...element.columns.slice(insertPos),
    ];

    onChange({ ...element });
  };

  const addGarage = () => {
    const copy = floorTempltate.map((col) => ({ ...col }));
    copy[0].value = element.value[0][0].value - 1;
    element.value = [copy, ...element.value];
    onChange(element);
  };

  const addFloor = () => {
    const copy = floorTempltate.map((col) => ({ ...col }));
    copy[0].value = element.value[element.value.length - 1][0].value + 1;
    element.value = [...element.value, copy];
    onChange(element);
  };

  const addCustomFloor = (value) => {
    const newRow = floorTempltate.map((col) => ({ ...col }));
    newRow[0].value = value;

    const exists = element.value.some((row) => row[0].value === value);
    if (exists) {
      setError(`Kondygnacja ${value} już istnieje.`);
      return;
    }

    const insertIndex = element.value.findIndex((row) => row[0].value > value);

    let newArray;
    if (insertIndex === -1) {
      newArray = [...element.value, newRow];
    } else {
      newArray = [
        ...element.value.slice(0, insertIndex),
        newRow,
        ...element.value.slice(insertIndex),
      ];
    }

    element.value = newArray;
    onChange(element);
  };

  // Usuwanie kolumny z wybranej sekcji (rowSpan)
  const removeColumnFromSection = (headerIndex, subIndex) => {
    // policz globalny indeks startu sekcji
    let startIdx = 0;
    for (let i = 0; i < headerIndex; i++) {
      const h = header[i];
      startIdx += h.type === "text" ? 1 : h.value?.length ?? 0;
    }
    const targetIdx = startIdx + subIndex; // globalny indeks kolumny do usunięcia

    // 1) zaktualizuj nagłówek: usuń subkolumnę
    header[headerIndex].value = header[headerIndex].value.filter(
      (_s, i) => i !== subIndex
    );

    element.columns = element.columns.filter((_c, i) => i !== targetIdx);

    // 2) usuń komórkę z każdej wartości/wiersza (scalenie obejmujące kolumnę jest rozdzielane)
    element.value = element.value.map((source) => {
      const row = splitMergeAt(source, targetIdx);
      const before = row.slice(0, targetIdx);
      const after = row.slice(targetIdx + 1);
      return [...before, ...after];
    });

    onChange({ ...element });
  };

  const promptBeforeSectionDelete = (sectionElement) => {
    if (window.confirm("Czy na pewno chcesz usunąć tę sekcję?")) {
      onDelete(sectionElement);
    }
  }

  return (
    <div className={element.class}>
      <Row>
        <Col>
        <Form.Control
          type="text"
          value={element.title}
          className="border-0 fs-4"
          onChange={(e) => {
            element.title = e.target.value;
            onChange(element);
          }}
        />
        </Col>
        <Col xs="auto">
        <Button variant="outline-danger" size="sm" onClick={() => promptBeforeSectionDelete(element)}>
          <FontAwesomeIcon icon={faTrash} /> Usuń sekcję
        </Button>
      </Col>
      </Row>
      <div className="aeration_table">
        <Table striped bordered hover className="aeration-table" size="sm">
          <thead>
            {/* Pierwszy wiersz nagłówków */}
            <tr>
              {header.map((h, index) =>
                h.type === "text" ? (
                  <ThText
                    key={index}
                    onChange={(event) => onChangeHeaderLabel(index, event)}
                    text={h.label}
                    className="text-center thick-border-right"
                  />
                ) : h.type === "rowSpan" ? (
                  <ThSpan
                    key={index}
                    label={h.label}
                    colSpan={h.value?.length ?? 0} // dynamiczne colSpan
                    onChange={(event) => onChangeHeaderLabel(index, event)}
                    onAdd={() => addColumnToSection(index)} // ⬅️ przycisk „+”
                  />
                ) : null
              )}
            </tr>

            {/* Drugi wiersz (subnagłówki sekcji) */}
            <tr className="text-center">
              {header.map((h, index) =>
                h.type === "rowSpan"
                  ? h.value.map((sub, subIndex) => (
                      <th
                        key={`${index}-${subIndex}`}
                        className={`text-center p-0 ${
                          isSectionEnd(subIndex, h) ? "thick-border-right" : ""
                        }`}
                      >
                        <div className="d-flex align-items-center gap-1">
                          <Form.Control
                            className={"border-0 p-1 bg-transparent aeration-header-control"}
                            as="textarea"
                            value={sub.label}
                            onChange={(event) =>
                              onChangeSubHeaderLabel(index, subIndex, event)
                            }
                          />
                          {/* „+” w kolumnie 1 (netto) - wstawia kolumnę zaraz za nią */}
                          {sections.indexOf(index) + subIndex === 1 && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="p-1 me-1 d-inline-flex align-items-center justify-content-center"
                              title="Dodaj kolumnę"
                              onClick={() => addColumnToSection(index, subIndex + 1)}
                            >
                              +
                            </Button>
                          )}
                        </div>
                      </th>
                    ))
                  : null
              )}
            </tr>
          </thead>
          <tbody>
            {values.map((row, index) => {
              return getRow(index);
            })}
            <tr>
              {header.map((h, hIndex) =>
                h.type === "text" ? (
                  <td
                    key={`del-${hIndex}`}
                    className="p-0 border-2 thick-border-right"
                  />
                ) : h.type === "rowSpan" ? (
                  h.value.map((_, subIndex) => (
                    <td
                      key={`del-${hIndex}-${subIndex}`}
                      className={`p-1 border-2 ${
                        isSectionEnd(subIndex, h) ? "thick-border-right" : ""
                      }`}
                    >
                      {isSectionEnd(subIndex, h) ||
                      _.deleteable === false ? null : (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() =>
                            removeColumnFromSection(hIndex, subIndex)
                          }
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      )}
                    </td>
                  ))
                ) : null
              )}
              {/* kolumna z przyciskiem usuwania wiersza – pusty "wypełniacz" */}
              <td />
            </tr>
          </tbody>
        </Table>
      </div>

      {error && (
        <Alert variant="danger" className="mt-2">
          {error}
        </Alert>
      )}

      <Alert variant="info" className="">
        <p>
          Dodaj numer piętra lub garażu. Wprowadź numer (-3, 21 itp.) lub użyj
          skrótu aby dodać piętro/garaż.
        </p>
        <InputGroup className="mb-3">
          <Button
            variant="outline-primary"
            id="button-addon1"
            onClick={() => {
              const floorNum = parseInt(customFloor, 10);
              if (!isNaN(floorNum)) {
                addCustomFloor(floorNum);
                setCustomFloor("");
              }
            }}
          >
            Dodaj
          </Button>
          <Form.Control
            placeholder="numer kondygnacji"
            aria-describedby="basic-addon2"
            value={customFloor}
            onChange={(e) => {
              setCustomFloor(e.target.value);
              if (error) setError(null);
            }}
            type="number"
            min={-10}
            max={30}
          />
          <Button
            variant="outline-primary"
            id="button-addon2"
            onClick={() => addGarage()}
          >
            Dodaj garaż
          </Button>
          <Button
            variant="outline-primary"
            id="button-addon3"
            onClick={() => addFloor()}
          >
            Dodaj piętro
          </Button>
        </InputGroup>
      </Alert>

      <div className="d-flex flex-column">
        {element.measurement_criteria.map((criteria, idx) => (
          <MeasurementsCriteria key={idx} value={criteria} />
        ))}
      </div>
    </div>
  );
}
